import { cloneDeep, isNumber, isString, toInteger } from 'lodash';
import { v4 as uuid } from 'uuid';
import { getTrackFunc, type IEdgelessPreviewClearable } from '@/framework';
import { Layer, type IAddLayerArgs, type LayerInsertPosition } from '@/layers/layer';
import { getVectorLayerProps } from '@/layers/vector-layer/utils';
import { base64Kit } from '@/utils/base64-kit';
import { getImgInfoAsync, limitMaxImageInfo, loadImageAsync, svg2Png, uploadVectorFile } from '@/utils/file-kit';
import { glKit } from '@/utils/gl-kit';
import { computerBgRatio, getImgLayerProps } from '@/utils/layer-kit';
import { uploadFileToObs } from '@/utils/upload-kit';
import { parseVector } from '@/utils/vector-kit';
import {
    LayerType,
    MaterialIdsType,
    PluginType,
    StretchOptionType,
    type IAgentInfo,
    type IBackgroundLayerProps,
    type IExportPlugin,
    type IImageAiTextExtension,
    type IImageLayerProps,
    type IImageTransform,
    type ILayerProps,
} from '@/version-types';
import imageLayerBehavior from './behavior';
import {
    defaultImageTransform,
    getDefaultImageLayerData,
    getDefaultMaskImageData,
} from './constants/default-image-data';
import { ImageHistory } from './image-history';
import { HistoryType as ImageHistoryType } from './type';
import { applyResolvedImageReplacement, applyResolvedMask, getMaskRatio, resolveImageReplacement } from './utils/layer';

export { EdgelessImageLayer, type IImageCanvasResultOptions } from './edgeless-layer';

export * from './plugins';

export interface IAddImageLayerBaseArgs {
    url: string;
    top?: number;
    left?: number;
    materialId?: string | Record<MaterialIdsType, string[]>;
    imageTransform?: IImageTransform;
    isPhoto?: boolean;
    lockRatio?: boolean;
    opacity?: number;
    agent?: IAgentInfo;
}

export interface IAddImageLayerArgs extends IAddImageLayerBaseArgs {
    width?: number;
    height?: number;
    isAILayer?: boolean;
    aiTextExtension?: IImageAiTextExtension;
    fontMaterialId?: number;
    byMousePosition?: boolean;
    fixedCenter?: boolean;
    position?: LayerInsertPosition;
    isTempLayer?: boolean;
}

export interface IAddImageLayerKeepSizeArgs extends IAddImageLayerBaseArgs {
    width: number;
    height: number;
    naturalWidth?: number;
    naturalHeight?: number;
}

/** 图片图层异步更新事务控制项 */
export interface IUpdateImageLayerOptions {
    /** 异步计算完成后是否仍允许提交 */
    canCommit?: () => boolean;
    /** 提交完成后是否激活候选图层，默认 true */
    activateLayer?: boolean;
}

/** 基于最新正式图层更新时的事务控制项 */
export interface IUpdateLatestImageLayerOptions extends IUpdateImageLayerOptions {
    /** 是否写入历史，默认 true */
    pushHistory?: boolean;
    /** 可选；历史统计标识 */
    trackFunc?: string;
}

/** 管理图片图层的数据事务、资源处理和格式转换 */
export class ImageLayer extends Layer {
    private updateLayerQueues = new Map<string, Promise<void>>();
    private maskRequestGenerations = new Map<string, number>();

    /**
     * 创建图片图层领域实例
     *
     * @param editorIdentity - 所属编辑器实例标识
     */
    constructor(editorIdentity: string) {
        super(editorIdentity);
    }

    /**
     * 获取当前资源环境用于表示空图片内容的默认蒙版图片
     *
     * @returns 默认蒙版图片的资源信息
     */
    getDefaultMaskImage() {
        return getDefaultMaskImageData();
    }

    private enqueueLayerUpdate<T>(layerId: string, task: () => Promise<T>) {
        const previousTask = this.updateLayerQueues.get(layerId) ?? Promise.resolve();
        const result = previousTask.catch(() => undefined).then(task);
        const settledTask = result.then(
            () => undefined,
            () => undefined,
        );
        this.updateLayerQueues.set(layerId, settledTask);
        void settledTask.finally(() => {
            if (this.updateLayerQueues.get(layerId) === settledTask) {
                this.updateLayerQueues.delete(layerId);
            }
        });
        return result;
    }

    /**
     * 原子更新图片图层数据
     *
     * 同一图片图层的更新按调用顺序执行。该事务只负责正式数据、历史和激活状态，
     * 图片特效派生产物由统一 render 和保存前校验负责。
     * 副作用：更新正式图层，并按配置写入历史和激活目标图层。
     *
     * @param newLayer - 候选新图层
     * @param oldLayer - 可选；写入历史记录的旧图层
     * @param pushHistory - 是否写入历史
     * @param trackFunc - 可选；历史统计标识
     * @param options - 可选；异步提交有效性与激活状态控制
     * @returns 实际提交的独立图层数据
     */
    async updateLayer(
        newLayer: ILayerProps,
        oldLayer?: ILayerProps,
        pushHistory = true,
        trackFunc?: string,
        options: IUpdateImageLayerOptions = {},
    ) {
        const { activateLayer = true, canCommit } = options;
        const layerId = newLayer.id;
        const queuedLayer = cloneDeep(newLayer);
        const historyLayer = oldLayer ? cloneDeep(oldLayer) : undefined;

        return this.enqueueLayerUpdate(layerId, async () => {
            const candidate = cloneDeep(queuedLayer);

            if (canCommit && !canCommit()) {
                throw new Error(`Image layer update is no longer valid: ${layerId}`);
            }

            this.framework.service.frame.addOrUpdateLayer(candidate);
            if (pushHistory && historyLayer) {
                this.framework.service.history.write(historyLayer, candidate, trackFunc);
            }
            if (activateLayer) {
                this.framework.service.frame.setActiveLayer(candidate.id);
            }
            return cloneDeep(candidate);
        });
    }

    /**
     * 基于事务开始时的最新正式数据原子更新图片图层
     *
     * `updater` 必须保持同步，只修改当前操作拥有的字段；返回 undefined 时跳过提交。
     * 副作用：更新正式图层，并按配置写入历史和激活目标图层。
     *
     * @param layerId - 目标图片图层 ID
     * @param updater - 基于最新图层生成候选图层的同步函数
     * @param options - 历史、统计、有效性与激活控制项
     * @returns 实际提交的独立图层数据；图层不存在或 updater 跳过时返回 undefined
     */
    async updateLayerFromLatest(
        layerId: string,
        updater: (latestLayer: ILayerProps) => ILayerProps | undefined,
        options: IUpdateLatestImageLayerOptions = {},
    ) {
        const { activateLayer = true, canCommit, pushHistory = true, trackFunc } = options;

        return this.enqueueLayerUpdate(layerId, async () => {
            if (canCommit && !canCommit()) {
                throw new Error(`Image layer update is no longer valid: ${layerId}`);
            }

            const latestLayer = this.framework.service.flatLayer.getLayer(layerId, true);
            if (!latestLayer) return;
            const historyLayer = cloneDeep(latestLayer);
            const candidate = updater(cloneDeep(latestLayer));
            if (!candidate) return;
            if (candidate.id !== layerId) {
                throw new Error(`Image layer updater changed layer id: ${layerId} -> ${candidate.id}`);
            }

            this.framework.service.frame.addOrUpdateLayer(candidate);
            if (pushHistory) {
                this.framework.service.history.write(historyLayer, candidate, trackFunc);
            }
            if (activateLayer) {
                this.framework.service.frame.setActiveLayer(candidate.id);
            }
            return cloneDeep(candidate);
        });
    }

    /**
     * 设置图层蒙版
     *
     * 同一图层存在并发蒙版请求时只提交最后一次有效请求。
     * 副作用：更新正式图层、写入蒙版历史并激活目标图层。
     *
     * @param id - 图层 ID
     * @param mask - 蒙版属性；`mask.url` 蒙版链接；`mask.materialId` 蒙版素材 ID
     * @returns 蒙版处理与有效提交完成后解决的 Promise；图层不存在或请求失效时直接完成
     */
    async setMask(id: string, { url, materialId }: { url: string; materialId: string }) {
        const requestGenerations = (this.maskRequestGenerations ??= new Map<string, number>());
        const generation = (requestGenerations.get(id) ?? 0) + 1;
        requestGenerations.set(id, generation);
        const maskRatio = await getMaskRatio(url);
        if (requestGenerations.get(id) !== generation) return;

        await this.updateLayerFromLatest(
            id,
            (latestLayer) => {
                applyResolvedMask(latestLayer, url, materialId, maskRatio);
                return latestLayer;
            },
            {
                canCommit: () => requestGenerations.get(id) === generation,
                trackFunc: 'mask',
            },
        );
    }

    private clearEdgelessPreview(layerId: string) {
        const obj = this.framework.service.edgelessCanvas.getByLayerId(
            layerId,
        ) as unknown as IEdgelessPreviewClearable | null;
        obj?.clearPreviewResource?.({ rerender: false });
    }

    /**
     * 清理图片 GL 临时预览并恢复正式图层渲染
     *
     * 会静默清理画布对象的本地预览资源、关闭图层 GL 标记并重新派发正式图层，
     * 由统一的图层变更监听触发一次画布刷新。
     * 副作用：清理目标图层的本地预览和 GL 状态，并重新派发正式图层。
     *
     * @param layerId - 图层 ID
     */
    clearImageGLPreview(layerId: string) {
        const layer = this.framework.service.flatLayer.getLayer(layerId, true);
        if (!layer) return;
        this.clearEdgelessPreview(layerId);
        glKit.clearGL(layer);
        this.framework.service.frame.addOrUpdateLayer(layer);
    }

    /**
     * 处理图片资源并替换指定图片图层
     *
     * 副作用：更新正式图层，按配置写入历史，并激活替换后的图层。
     *
     * @param url - 新图片地址
     * @param layerId - 目标图层 ID
     * @param imageWidth - 可选；图片宽度
     * @param imageHeight - 可选；图片高度
     * @param naturalWidth - 可选；图片原始宽度
     * @param naturalHeight - 可选；图片原始高度
     * @param pushHistory - 是否写入历史
     * @param trackFunc - 可选；历史统计标识
     * @param isAi - 是否标记为 AI 图层
     * @param extendOldLayerData - 可选；合并到新图层的字段
     * @param useNewSize - 是否直接使用新尺寸和位置策略
     * @returns 替换前后的图层快照；目标不存在时返回 undefined
     */
    async processImgAndReplaceUrl(
        url: string,
        layerId: string,
        imageWidth?: number,
        imageHeight?: number,
        naturalWidth?: number,
        naturalHeight?: number,
        pushHistory = true,
        trackFunc?: string,
        isAi = false,
        extendOldLayerData?: Partial<ILayerProps>,
        useNewSize = false,
    ) {
        const { width: stageWidth } = this.framework.service.frame.getActiveFrameSize();

        let newImageWidth = 0;
        let newImageHeight = 0;

        // 如果没有传入图片信息就自己获取
        if (imageWidth === undefined || imageHeight === undefined) {
            // 获取图片信息
            const { width: imageWidth, height: imageHeight } = await getImgInfoAsync(url);
            newImageWidth = imageWidth;
            newImageHeight = imageHeight;
        } else {
            newImageWidth = imageWidth;
            newImageHeight = imageHeight;
        }

        // 如果宽大于1.5倍舞台宽高 就按1.5倍舞台宽高计算
        const limitResult = limitMaxImageInfo(stageWidth, url, newImageWidth, newImageHeight);
        newImageWidth = limitResult.naturalWidth;
        newImageHeight = limitResult.naturalHeight;
        url = limitResult.url;

        const trackLayer = this.framework.service.flatLayer.getLayer(layerId);
        if (!trackLayer) return;
        let oldInfo: ILayerProps | undefined;
        const newInfo = await this.updateLayerFromLatest(
            layerId,
            (latestLayer) => {
                oldInfo = cloneDeep(latestLayer);
                const newLayer = cloneDeep(latestLayer);
                if (isAi) {
                    newLayer.isAILayer = true;
                }
                const imgInfo = newLayer.data as IImageLayerProps;
                imgInfo.naturalWidth = naturalWidth || newImageWidth;
                imgInfo.naturalHeight = naturalHeight || newImageHeight;
                imgInfo.url = url;
                imgInfo.cutoutMaskURL = '';
                newLayer.width = newImageWidth;
                newLayer.height = newImageHeight;
                const imageTransform: IImageTransform = {
                    ...cloneDeep(defaultImageTransform),
                    widthRatio: 1,
                    heightRatio: 1,
                };

                if (!useNewSize) {
                    newLayer.left = latestLayer.left - (newLayer.width - latestLayer.width) / 2;
                    newLayer.top = latestLayer.top - (newLayer.height - latestLayer.height) / 2;
                }

                imgInfo.imageTransform = imageTransform;
                return Object.assign(
                    {
                        ...newLayer,
                        data: {
                            ...imgInfo,
                        },
                    },
                    extendOldLayerData,
                );
            },
            {
                pushHistory,
                trackFunc: trackFunc || getTrackFunc(this.identity, trackLayer, isAi ? 'aiReplace' : 'replace'),
            },
        );
        if (!oldInfo || !newInfo) return;
        return { oldInfo, newInfo };
    }

    /**
     * 添加图片图层
     *
     * 副作用：按定位策略向当前画布添加图片图层，并按配置写入新增历史和激活新图层。
     *
     * @param args - 图片资源、尺寸、位置和素材信息
     * @param fitWithStage - 是否按舞台尺寸约束图片
     * @param pushHistory - 是否写入新增历史
     * @returns 新增的图片图层
     */
    async addImageLayer(args: IAddImageLayerArgs, fitWithStage = true, pushHistory = true) {
        let { url, width: naturalWidth, height: naturalHeight, top, left } = args;
        const {
            materialId,
            isPhoto,
            isAILayer,
            opacity,
            imageTransform,
            aiTextExtension,
            fontMaterialId,
            byMousePosition = false,
            fixedCenter = false,
            position,
            isTempLayer = false,
            lockRatio = true,
            agent,
        } = args;

        const { width: stageWidth, height: stageHeight } = this.framework.service.frame.getActiveFrameSize();

        let width = naturalWidth;
        let height = naturalHeight;

        if (fitWithStage || !width || !height) {
            const stageRatio = stageWidth / stageHeight;
            if (!naturalWidth || !naturalHeight) {
                const { width, height } = await loadImageAsync(url);
                naturalWidth = width;
                naturalHeight = height;
            }

            // 如果宽大于1.5倍舞台宽高 就按1.5倍舞台宽高计算
            const limitResult = limitMaxImageInfo(stageWidth, url, naturalWidth, naturalHeight);
            naturalWidth = limitResult.naturalWidth;
            naturalHeight = limitResult.naturalHeight;
            url = limitResult.url;

            const ratio = naturalWidth / naturalHeight;

            if (stageRatio > ratio) {
                height = toInteger(stageHeight / 2);
                width = toInteger(naturalWidth / (naturalHeight / height));
            } else {
                width = toInteger(stageWidth / 2);
                height = toInteger(naturalHeight / (naturalWidth / width));
            }
        }

        if (fixedCenter && !byMousePosition) {
            const centerPoint = {
                x: toInteger(stageWidth / 2),
                y: toInteger(stageHeight / 2),
            };
            left = centerPoint.x - width / 2;
            top = centerPoint.y - height / 2;
        }

        // 获取图片图层默认数据
        const imageLayerData = getDefaultImageLayerData(
            url,
            width,
            height,
            left ?? 0,
            top ?? 0,
            materialId,
            naturalWidth,
            naturalHeight,
            isPhoto,
        );

        // 获取图片图层数据
        const layer = getImgLayerProps(imageLayerData);

        let finalData: ILayerProps | IAddLayerArgs = layer;

        layer.shine = false;
        if (opacity) {
            layer.opacity = opacity;
        }
        if (imageTransform) {
            (layer.data as IImageLayerProps).imageTransform = imageTransform;
        }

        // 添加花字属性
        if (aiTextExtension) {
            (layer.data as IImageLayerProps).aiTextExtension = aiTextExtension;
            if (fontMaterialId) {
                layer.materialIds = layer.materialIds || {};
                layer.materialIds[MaterialIdsType.FONT_FAMILY] = [`${fontMaterialId}`];
            }
            layer.isAILayer = true;
        }

        if (isAILayer) {
            layer.isAILayer = true;
        }

        if (isTempLayer) {
            layer.__isTempLayer = true;
        }

        layer.extraInfo = {
            ...layer.extraInfo,
            lockRatio,
            agent,
        };

        if (!pushHistory) {
            finalData = {
                layer: layer,
                isActiveNewLayer: true,
                isWriteHistory: pushHistory,
            };
        }

        if (position) {
            this.addByInsertPosition(finalData, position);
        } else if (byMousePosition) {
            this.addByMousePosition(finalData);
        } else if (top !== undefined && left !== undefined) {
            this.add(finalData);
        } else {
            this.addByDiagonal(finalData);
        }

        return layer;
    }

    /**
     * 按指定显示尺寸添加图片图层
     *
     * 未提供原始尺寸时会加载图片资源获取；显示宽高始终保持调用方传入值。
     * 副作用：向当前画布添加图片图层、写入新增历史并激活新图层。
     *
     * @param args - 图片资源、固定显示尺寸、位置和素材信息
     * @returns 新增的图片图层
     */
    async addImageLayerKeepSize(args: IAddImageLayerKeepSizeArgs) {
        const { url, width, height, imageTransform, top, left, materialId, isPhoto, lockRatio, opacity, agent } = args;

        let { naturalWidth, naturalHeight } = args;

        if (!naturalHeight || !naturalWidth) {
            const img = await loadImageAsync(url);
            naturalHeight = img.height;
            naturalWidth = img.width;
        }

        // 获取图片图层默认数据
        const imageLayerData = getDefaultImageLayerData(
            url,
            width,
            height,
            left ?? 0,
            top ?? 0,
            materialId,
            naturalWidth,
            naturalHeight,
            isPhoto,
        );

        const layer = getImgLayerProps(imageLayerData);

        layer.extraInfo = {
            ...layer.extraInfo,
            lockRatio,
            agent,
        };
        layer.shine = false;
        if (opacity) {
            layer.opacity = opacity;
        }
        if (imageTransform) {
            (layer.data as IImageLayerProps).imageTransform = imageTransform;
        }
        const finalData = {
            layer: layer,
            isActiveNewLayer: true,
            isWriteHistory: true,
        };

        if (top !== undefined && left !== undefined) {
            this.add(finalData);
        } else {
            this.addByDiagonal(finalData);
        }

        return layer;
    }

    /**
     * 替换图片图层
     *
     * 副作用：更新正式图层，并按配置写入历史和激活目标图层。
     *
     * @param url - 图片地址
     * @param layerId - 图层 ID
     * @param imageWidth - 可选；替换的图片宽度
     * @param imageHeight - 可选；替换的图片高度
     * @param pushHistory - 可选；是否添加到历史记录中；默认值为 `true`
     * @param materialIds - 可选；素材 ID 列表
     * @param prompt - 可选；提示语
     * @param trackFunc - 可选；统计 Key
     * @param isAi - 可选；是否为 AI 图层；默认值为 `false`
     * @param extendOldLayerData - 可选；扩展的旧图层数据
     * @param resetSize - 是否重置图层尺寸
     * @returns 替换前后的图层快照；目标图层不存在时返回 undefined
     */
    async replaceImage(
        url: string,
        layerId: string,
        imageWidth?: number,
        imageHeight?: number,
        pushHistory = true,
        materialIds: Record<string, string[]> = {},
        prompt = '',
        trackFunc?: string,
        isAi = false,
        extendOldLayerData?: Partial<ILayerProps>,
        resetSize = false,
    ) {
        const { width: stageWidth } = this.framework.service.frame.getActiveFrameSize();
        const replacement = await resolveImageReplacement(url, stageWidth, imageWidth, imageHeight);
        const trackLayer = this.framework.service.flatLayer.getLayer(layerId);
        if (!trackLayer) return;
        let oldInfo: ILayerProps | undefined;
        const newInfo = await this.updateLayerFromLatest(
            layerId,
            (latestLayer) => {
                oldInfo = cloneDeep(latestLayer);
                if (isAi) {
                    latestLayer.isAILayer = true;
                }
                const candidate = applyResolvedImageReplacement(
                    latestLayer,
                    replacement,
                    prompt,
                    materialIds,
                    resetSize,
                );
                return Object.assign(candidate, extendOldLayerData);
            },
            {
                activateLayer: pushHistory,
                pushHistory,
                trackFunc:
                    trackFunc || getTrackFunc(this.framework.identity, trackLayer, isAi ? 'aiReplace' : 'replace'),
            },
        );

        if (!oldInfo || !newInfo) return;
        return { oldInfo, newInfo };
    }

    /**
     * 处理 AI 修改图片
     *
     * 副作用：更新正式图层，并按配置写入历史和激活目标图层。
     *
     * @param url - 图片地址
     * @param layerId - 图层 ID
     * @param pushHistory - 可选；是否推入历史；默认值为 `true`
     * @param trackFunc - 可选；统计 Key
     * @param isAi - 可选；是否为 AI 图层；默认值为 `false`
     * @param extendOldLayerData - 可选；扩展旧图层数据
     * @returns 修改前后的图层快照；目标图层不存在时返回 undefined
     */
    async processAIModifyImage(
        url: string,
        layerId: string,
        pushHistory = true,
        trackFunc?: string,
        isAi = false,
        extendOldLayerData?: Partial<ILayerProps>,
    ) {
        const layer = this.framework.service.flatLayer.getLayer(layerId, true);

        if (!layer) {
            return;
        }

        if (isAi) {
            layer.isAILayer = true;
        }

        let newLayer = cloneDeep(layer);

        const imgInfo = newLayer?.data as IImageLayerProps;

        imgInfo.url = url;
        imgInfo.cutoutMaskURL = '';

        newLayer = {
            ...newLayer,
            data: {
                ...imgInfo,
            },
        };

        newLayer = Object.assign(newLayer, extendOldLayerData);
        const oldInfo = layer;
        const newInfo = newLayer;

        this.framework.service.frame.addOrUpdateLayer(newLayer);

        if (pushHistory) {
            this.framework.service.history.write(
                layer,
                trackFunc || getTrackFunc(this.framework.identity, newLayer, isAi ? 'aiReplace' : 'replace'),
            );
            this.framework.service.frame.setActiveLayer(layer.id);
        }

        return { oldInfo, newInfo };
    }

    /**
     * 应用 AI 扩图结果
     *
     * 非主图层会在本方法内提交正式数据；主图层只返回候选快照，由调用方完成舞台级提交。
     * 副作用：非主图层按配置更新正式图层、写入历史并激活目标图层。
     *
     * @param url - 扩图结果地址
     * @param layerId - 目标图层 ID
     * @param imageWidth - 结果图片宽度
     * @param imageHeight - 结果图片高度
     * @param pushHistory - 是否写入历史
     * @param newWidth - 目标图层宽度
     * @param newHeight - 目标图层高度
     * @param _ratio - 保留的扩图比例参数；当前几何计算使用目标宽高
     * @param trackFunc - 可选；历史统计标识
     * @param isAi - 是否标记为 AI 图层
     * @param extendOldLayerData - 可选；合并到新图层的字段
     * @param useNewSize - 是否直接采用新尺寸并移动到舞台原点
     * @returns 替换前后的图层快照；目标不存在时返回 undefined
     */
    async processAIExpendImage(
        url: string,
        layerId: string,
        imageWidth: number,
        imageHeight: number,
        pushHistory = true,
        newWidth: number,
        newHeight: number,
        _ratio: string,
        trackFunc?: string,
        isAi = false,
        extendOldLayerData?: Partial<ILayerProps>,
        useNewSize = false,
    ) {
        const layer = this.framework.service.flatLayer.getLayer(layerId, true);

        if (!layer) {
            return;
        }

        if (isAi) {
            layer.isAILayer = true;
        }

        const { width: stageWidth } = this.framework.service.frame.getActiveFrameSize();

        let newImageWidth = imageWidth;
        let newImageHeight = imageHeight;

        if (!useNewSize) {
            // 如果宽大于1.5倍舞台宽高 就按1.5倍舞台宽高计算
            const limitResult = limitMaxImageInfo(stageWidth, url, newImageWidth, newImageHeight);
            newImageWidth = limitResult.naturalWidth;
            newImageHeight = limitResult.naturalHeight;
            url = limitResult.url;
        }

        let newLayer = cloneDeep(layer);

        const imgInfo = newLayer?.data as IImageLayerProps;

        // 每次都要重置imageTransform
        imgInfo.naturalWidth = newImageWidth;
        imgInfo.naturalHeight = newImageHeight;
        imgInfo.url = url;
        imgInfo.cutoutMaskURL = '';
        let imageTransform: IImageTransform = {
            ...cloneDeep(defaultImageTransform),
            widthRatio: 1,
            heightRatio: 1,
        };
        imageTransform = Object.assign(
            imageTransform,
            computerBgRatio({
                imgWidth: newImageWidth,
                imgHeight: newImageHeight,
                stageHeight: newHeight,
                stageWidth: newWidth,
            }),
        );

        imgInfo.imageTransform = imageTransform;

        newLayer = {
            ...newLayer,
            data: {
                ...imgInfo,
            },
        };
        const oldWidth = newLayer.width;
        const oldHeight = newLayer.height;

        // let [ratioWidth, ratioHeight] = [oldWidth, oldHeight];

        // if (ratio !== '0') {
        // [ratioWidth, ratioHeight] = ratio.split(':').map((str) => parseInt(str));
        // }

        newLayer.width = newWidth;
        newLayer.height = newHeight;

        if (!useNewSize) {
            newLayer.left = newLayer.left - (newLayer.width - oldWidth) / 2;
            newLayer.top = newLayer.top - (newLayer.height - oldHeight) / 2;
        } else {
            newLayer.left = 0;
            newLayer.top = 0;
        }

        newLayer = Object.assign(newLayer, extendOldLayerData);

        const oldInfo = layer;
        const newInfo = newLayer;

        if (layer.layerType !== LayerType.MAIN_LAYER) {
            this.framework.service.frame.addOrUpdateLayer(cloneDeep(newLayer));
            if (pushHistory) {
                this.framework.service.history.write(
                    cloneDeep(layer),
                    trackFunc || getTrackFunc(this.framework.identity, newLayer, isAi ? 'aiReplace' : 'replace'),
                );
                this.framework.service.frame.setActiveLayer(layer.id);
            }
        }

        return { oldInfo, newInfo };
    }

    /**
     * 清空图片内容并保留图层容器
     *
     * 清空图片相关素材 ID 和特效，将图片资源恢复为当前环境的默认蒙版图片。
     * 副作用：更新正式图层、写入一次历史并激活目标图层。
     *
     * @param layerId - 图层 ID
     * @param trackFunc - 可选；历史统计标识
     * @returns 清空操作完成后解决的 Promise；目标图层不存在时直接完成
     */
    async clearImageContent(layerId: string, trackFunc?: string) {
        const oldLayer = this.framework.service.flatLayer.getLayer(layerId, true);
        if (!oldLayer) {
            return;
        }
        const newLayer = cloneDeep(oldLayer);
        newLayer.materialIds = newLayer.materialIds || {};
        newLayer.materialIds[MaterialIdsType.FUNC_LIST] = [];
        newLayer.materialIds[MaterialIdsType.IMAGE_MATERIAL] = [];
        newLayer.materialIds[MaterialIdsType.PHOTO_MATERIAL] = [];
        newLayer.materialIds[MaterialIdsType.FILTER] = [];
        (newLayer.data as IImageLayerProps).url = this.getDefaultMaskImage().url;
        (newLayer.data as IImageLayerProps).effect = null;
        this.framework.service.frame.addOrUpdateLayer(newLayer);
        this.framework.service.history.write(oldLayer, newLayer, trackFunc);
        this.framework.service.frame.setActiveLayer(layerId);
    }

    /**
     * 图片转背景
     *
     * 使用图片内容和素材信息更新当前背景，同时删除来源图片图层。
     * 副作用：删除来源图层、清空当前选中、更新背景图层并写入组合历史。
     *
     * @param layerId - 图层 ID
     * @param _deleteImage - 兼容旧调用；当前转换始终删除来源图片图层
     * @param materialIds - 可选；素材 ID 列表
     */
    convertImageToBackground(layerId: string, _deleteImage = true, materialIds?: Record<string, string[]>) {
        const layer = this.framework.service.flatLayer.getLayer(layerId, true);
        const newLayer = cloneDeep(layer);
        if (!newLayer) return;
        // 删除图片后无法再从 flatLayer 查询所属画布，须提前保留组合历史的画布 ID。
        const originIndex = this.framework.service.frame.getLayerFrameId(layerId);
        if (!isNumber(originIndex)) return;
        const bgLayer = this.framework.service.frame.getBackgroundLayer(
            this.framework.service.frame.getActiveFrameId(),
        );
        const newBgLayer = cloneDeep(bgLayer)!;
        /**
         * step1: 把当前图片图层的url 设置成 背景的url
         * step2: 删除当前图片图层
         * step3: 写入”组合历史“数据
         */
        const { data: imgData } = newLayer;
        const { url, naturalWidth, naturalHeight } = imgData as IImageLayerProps;
        const { widthRatio, heightRatio } = computerBgRatio({
            imgWidth: naturalWidth,
            imgHeight: naturalHeight,
            stageHeight: newBgLayer.height,
            stageWidth: newBgLayer.width,
        });
        const bgData = newBgLayer.data as IBackgroundLayerProps;
        const newBgData = {
            ...bgData,
            naturalHeight,
            naturalWidth,
            url: base64Kit.getRemoteUrl(url),
            imageTransform: {
                ...(imgData as IImageLayerProps).imageTransform,
                widthRatio,
                heightRatio,
            },
            stretchOption: StretchOptionType.cover,
        };
        newBgLayer.data = newBgData;
        newBgLayer.materialIds = cloneDeep(newLayer.materialIds);
        // 清空蒙版素材
        delete newBgLayer.materialIds?.[MaterialIdsType.MASK];
        newBgLayer.opacity = newLayer.opacity;

        // 追加素材
        if (materialIds) {
            Object.entries(materialIds).forEach(([key, value]) => {
                if (value.length) newBgLayer.materialIds?.[key]?.push(...value);
            });
        }

        this.framework.service.frame.delLayer(layerId);
        this.framework.service.frame.delActiveLayer();
        this.framework.service.frame.addOrUpdateLayer(newBgLayer);

        this.framework.service.history.write({
            type: ImageHistoryType.CONVERT_IMAGE_TO_BACKGROUND,
            data: {
                item: {
                    oldLayer: layer,
                    layerIndex: originIndex,
                    newBgLayer: cloneDeep(newBgLayer),
                    oldBgLayer: cloneDeep(bgLayer),
                },
            },
            originIndex,
        });
    }

    /**
     * 判断目标是否为照片图层
     *
     * @param args - 图层 ID 或图层数据
     * @returns 是照片图层时返回 true；目标不存在时返回 false；behavior 未实现判断时返回 undefined
     */
    checkIsPhoto(args: string | ILayerProps) {
        return this.framework.service.flatLayer.checkIsPhoto(
            isString(args) ? this.framework.service.flatLayer.getLayer(args) : args,
        );
    }

    /**
     * 判断目标是否为 AI 花字图层
     *
     * @param args - 图层 ID 或图层数据
     * @returns 是 AI 花字图层时返回 true；目标不存在时返回 false；behavior 未实现判断时返回 undefined
     */
    checkIsAiText(args: string | ILayerProps) {
        return this.framework.service.flatLayer.checkIsAiText(
            isString(args) ? this.framework.service.flatLayer.getLayer(args) : args,
        );
    }

    /**
     * 扩展图片并更换背景
     *
     * 副作用：写入同时包含图片图层和背景图层变更的组合历史；不负责提交图层数据。
     *
     * @param newLayer - 新图层
     * @param oldLayer - 旧图层
     * @param oldBgLayer - 旧背景图层
     * @param newBgLayer - 新背景图层
     */
    expendImageAndChangeBg(
        newLayer: ILayerProps,
        oldLayer: ILayerProps,
        oldBgLayer: ILayerProps,
        newBgLayer: ILayerProps,
    ) {
        this.framework.service.history.write({
            type: ImageHistoryType.EXPEND_IMAGE_AND_CHANGE_BG,
            data: {
                item: {
                    newLayer: cloneDeep(newLayer),
                    oldLayer: cloneDeep(oldLayer),
                    oldBgLayer: cloneDeep(oldBgLayer),
                    newBgLayer: cloneDeep(newBgLayer),
                },
            },
            originIndex: oldLayer.__originIndex,
        });
    }

    /**
     * 使用矢量文件生成替换图层
     *
     * 上传原始矢量文件和解析后的矢量内容，并按填充方式计算结果尺寸。
     * 副作用：为传入图层生成新 ID；`fillType` 为 `full` 时还会原地调整其位置和尺寸。
     *
     * @param layer - 待替换的图片图层；方法可能原地修改其 ID 和几何信息
     * @param file - 矢量图片文件
     * @param fillType - 可选；填充方式，`full` 表示完整显示矢量内容
     * @returns 基于上传结果创建的新矢量图层
     */
    async replaceImageLayerByVectorFile(layer: ILayerProps, file: File, fillType?: string) {
        const { svgEl } = await svg2Png(file);
        const svgFile = await uploadFileToObs(file);
        const { text, info } = await parseVector(svgEl);
        const imageUrl = await uploadVectorFile(text);
        layer.id = uuid();
        const svgInfo = {
            image: imageUrl.url,
            info,
            file: svgFile.url,
        };

        let newWidth = layer.width;
        let newHeight = layer.height;

        if (fillType === 'full') {
            const newImageWidth = info.naturalWidth;
            const newImageHeight = info.naturalHeight;

            if (newImageWidth / layer.width > newImageHeight / layer.height) {
                newWidth = layer.width;
                newHeight = (layer.width * newImageHeight) / newImageWidth;
            } else {
                newHeight = layer.height;
                newWidth = (layer.height * newImageWidth) / newImageHeight;
            }

            layer.left = layer.left + layer.width / 2 - newWidth / 2;
            layer.top = layer.top + layer.height / 2 - newHeight / 2;
            layer.width = newWidth;
            layer.height = newHeight;
        }

        const vectorLayer = getVectorLayerProps(
            {
                url: svgInfo.image,
                left: layer.left,
                top: layer.top,
                width: newWidth,
                height: newHeight,
            },
            info,
        );
        return vectorLayer;
    }

    /**
     * 使用图片文件原地更新候选图层
     *
     * 上传图片并根据填充方式更新图片数据和几何信息；本方法不提交正式图层或历史。
     * 副作用：原地修改传入图层的数据、位置、尺寸、AI 标记和 ID。
     *
     * @param layer - 待更新的候选图片图层
     * @param file - 图片文件
     * @param fillType - 填充方式；`cover` 保持容器并计算裁剪比例，其他值完整显示图片
     * @returns 图片上传和候选图层更新完成后解决的 Promise
     */
    async replaceImageLayerByImageFile(layer: ILayerProps, file: File, fillType = 'cover') {
        const { url: remoteUrl } = await uploadFileToObs(file);

        const { width: newImageWidth, height: newImageHeight } = await getImgInfoAsync(remoteUrl);

        const imgInfo = layer.data as IImageLayerProps;
        // let { stretchOption } = imgInfo;

        // 每次都要重置imageTransform
        imgInfo.naturalWidth = newImageWidth;
        imgInfo.naturalHeight = newImageHeight;
        imgInfo.url = remoteUrl;
        imgInfo.cutoutMaskURL = '';

        let imageTransform: IImageTransform = {
            ...cloneDeep(defaultImageTransform),
            widthRatio: 1,
            heightRatio: 1,
        };

        if (fillType === 'cover') {
            // 如果是自由模式还要计算下宽高比
            imageTransform = Object.assign(
                imageTransform,
                computerBgRatio({
                    imgWidth: newImageWidth,
                    imgHeight: newImageHeight,
                    stageHeight: layer.height,
                    stageWidth: layer.width,
                }),
            );
        } else {
            let newWidth = layer.width;
            let newHeight = layer.height;

            if (newImageWidth / layer.width > newImageHeight / layer.height) {
                newWidth = layer.width;
                newHeight = (layer.width * newImageHeight) / newImageWidth;
            } else {
                newHeight = layer.height;
                newWidth = (layer.height * newImageWidth) / newImageHeight;
            }

            layer.left = layer.left + layer.width / 2 - newWidth / 2;
            layer.top = layer.top + layer.height / 2 - newHeight / 2;
            layer.width = newWidth;
            layer.height = newHeight;
        }

        imgInfo.imageTransform = imageTransform;
        layer.isAILayer = false;

        layer.data = imgInfo;
        layer.id = uuid();
    }
}

/**
 * 图片图层插件
 */
export const imageLayerPlugin: IExportPlugin = {
    sort: 0,
    type: PluginType.LAYER,
    name: LayerType.IMAGE_LAYER,
    /**
     * 创建图片图层实例注册项
     *
     * @param args - 插件注册上下文
     * @returns 包含图片图层实例的注册项列表
     */
    createInstance({ identity }) {
        return [{ key: 'ImageLayer', value: new ImageLayer(identity) }];
    },
    /**
     * 创建图片图层行为定义
     *
     * @returns 图片图层行为定义
     */
    createBehavior() {
        return imageLayerBehavior;
    },
    /**
     * 创建图片图层自定义历史实例
     *
     * @param args - 插件注册上下文
     * @returns 图片图层历史实例列表
     */
    createHistory({ identity }) {
        return [new ImageHistory(identity)];
    },
};
